import { create } from 'zustand';
import apiClient from '../lib/api';

export interface FinopsCost {
    id: string;
    tenantId: string;
    provider: string;
    serviceName: string;
    amount: number;
    currency: string;
    billingPeriod?: string;
    status: string;
    createdAt: string;
    updatedAt: string;
}

interface FinopsState {
    costs: FinopsCost[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    fetchCosts: (filters?: any) => Promise<void>;
    createCost: (data: Partial<FinopsCost>) => Promise<void>;
    updateCost: (id: string, data: Partial<FinopsCost>) => Promise<void>;
}

export const useFinopsStore = create<FinopsState>((set, get) => ({
    costs: [],
    isLoading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
    },
    fetchCosts: async (filters?: any) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get('/money/finops', { params: filters });
            const { data, pagination } = response.data;
            set({ costs: data, pagination, isLoading: false });
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to fetch finops costs', isLoading: false });
        }
    },
    createCost: async (data: Partial<FinopsCost>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.post('/money/finops', data);
            const newCost = response.data;
            set((state) => ({
                costs: [newCost, ...state.costs],
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to create finops cost', isLoading: false });
            throw error;
        }
    },
    updateCost: async (id: string, data: Partial<FinopsCost>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.patch(`/money/finops/${id}`, data);
            const updatedCost = response.data;
            set((state) => ({
                costs: state.costs.map((o) => (o.id === id ? updatedCost : o)),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to update finops cost', isLoading: false });
            throw error;
        }
    },
}));
